import { destroyTokenCookies } from "@contexts/auth/AuthContext";
import axios, { AxiosError, AxiosHeaders, AxiosInstance } from "axios";
import { GetServerSidePropsContext } from "next";
import Router from "next/router";
import { parseCookies, setCookie } from "nookies";
import { api as apiAdmin } from "./apiClient";

export type TokenJWT = {
  name: string;
  permissions: string[];
  type: string;
  iat: number;
  exp: number;
  sub: string;
};

let isRefreshing = false;
let failedRequestsQueue: { onSuccess: (token: string) => void; onFailure: (err: AxiosError) => void }[] = [];

export const decodeToken = (token: string) => {
  const tokenParts = token.split(".");

  if (tokenParts.length !== 3) {
    throw new Error("Invalid token format");
  }
  const base64Url = tokenParts[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const decodedPayload = atob(base64);

  return JSON.parse(decodedPayload) as TokenJWT;
};

export const requestRefreshToken = async (apiInstance?: AxiosInstance, callback?: () => void, baseURL?: string) => {
  const api =
    apiInstance ??
    axios.create({
      baseURL: baseURL || process.env.NEXT_PUBLIC_ADMIN_BACKEND_URL,
    });
  const { "admin.refreshToken": oldRefreshToken } = parseCookies();

  apiAdmin
    .post("/users/refresh-token", {
      refreshToken: oldRefreshToken,
    })
    .then(response => {
      const { token, refreshToken } = response.data;

      api.defaults.headers["Authorization"] = `Bearer ${token}`;

      setCookie(undefined, "admin.token", token, {
        maxAge: 60 * 60 * 24 * 30, // 30 days
        path: "/", // What path will have access to the cookie. In this case, all of them.
      });

      setCookie(undefined, "admin.refreshToken", refreshToken, {
        maxAge: 60 * 60 * 24 * 30,
        path: "/",
      });

      failedRequestsQueue.forEach(request => request.onSuccess(token));
      failedRequestsQueue = [];
    })
    .catch(err => {
      failedRequestsQueue.forEach(request => request.onFailure(err));
      failedRequestsQueue = [];
      destroyTokenCookies();
      if (typeof window !== "undefined") {
        window.location.href = "/";
      }
    })
    .finally(() => {
      callback?.();
    });
};

export const setupAPIClient = (baseURL?: string) => {
  const api = axios.create({
    baseURL: baseURL || process.env.NEXT_PUBLIC_ADMIN_BACKEND_URL,
  });

  api.interceptors.request.use(async config => {
    let cookies = parseCookies();
    if (cookies && cookies !== undefined) {
      (config.headers as AxiosHeaders).set("Authorization", `Bearer ${cookies["admin.token"]}`);
    }
    return config;
  });

  api.interceptors.response.use(
    response => response,
    (error: AxiosError<{ message?: string }>) => {
      if (error.response?.status === 401) {
        if (error.response?.data?.message === "Invalid token!") {
          const originalConfig = error.config;

          if (!isRefreshing) {
            isRefreshing = true;
            requestRefreshToken(api, () => (isRefreshing = false));
          }

          return new Promise((resolve, reject) => {
            failedRequestsQueue.push({
              onSuccess: (token: string) => {
                if (originalConfig && originalConfig.url !== "/users/refresh-token") {
                  (originalConfig.headers as AxiosHeaders).set("Authorization", `Bearer ${token}`);

                  resolve(api(originalConfig));
                }
              },
              onFailure: (err: AxiosError) => {
                reject(err);
              },
            });
          });
        }
      }

      if (error.response?.status === 403 && Router.asPath !== "/sales") {
        if (typeof window !== "undefined") {
          window.location.href = "/";
        }
      }

      return Promise.reject(error);
    }
  );

  return api;
};

export const setupAPIServer = (ctx: GetServerSidePropsContext, baseURL?: string) => {
  const api = axios.create({
    baseURL: baseURL || process.env.NEXT_PUBLIC_ADMIN_BACKEND_URL,
  });

  api.interceptors.request.use(async config => {
    let cookies = parseCookies(ctx);
    if (cookies && cookies !== undefined) {
      (config.headers as AxiosHeaders).set("Authorization", `Bearer ${cookies["admin.token"]}`);
    }
    return config;
  });

  api.interceptors.response.use(
    response => response,
    (error: AxiosError<{ message?: string }>) => {
      if (error.response?.status === 401) {
        if (error.response?.data?.message === "Invalid token!") {
          const originalConfig = error.config;

          if (!isRefreshing) {
            isRefreshing = true;
            const apiAdminServer = setupAPIServer(ctx);
            requestRefreshToken(apiAdminServer, () => (isRefreshing = false));
          }

          return new Promise((resolve, reject) => {
            failedRequestsQueue.push({
              onSuccess: (token: string) => {
                if (originalConfig && originalConfig.url !== "/users/refresh-token") {
                  (originalConfig.headers as AxiosHeaders).set("Authorization", `Bearer ${token}`);

                  resolve(api(originalConfig));
                }
              },
              onFailure: (err: AxiosError) => {
                reject(err);
              },
            });
          });
        }
      }

      if (error.response?.status === 403 && Router.asPath !== "/sales") {
        if (typeof window !== "undefined") {
          window.location.href = "/";
        }
      }

      return Promise.reject(error);
    }
  );

  return api;
};
